/* eslint-disable @typescript-eslint/no-explicit-any */
import { IconChevronDown, IconChevronUp } from 'components/Icons'
import SkeletonBanner from 'components/Skeleton/SkeletonBanner'
import API from 'core/api'
import { LINK_MOVE_TYPE } from 'core/constants'
import { useOutsideClickEffect } from 'core/hooks/useOutsideClickEffect'
import { STORE_MARKETING_GTM } from 'core/store/analysis'
import { moveToLink } from 'core/util/url'
import { useEffect, useRef, useState } from 'react'
import { useAsync } from 'react-async'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'

enum TextBannerLinkTypes {
  NONE = 'none',
  MOVE = 'move',
  OPEN = 'open',
}

interface SectionTextBannerProps {}

const SectionTextBanner: React.FC<SectionTextBannerProps> = () => {
  const orderType = 'show'
  const { data, isLoading } = useAsync({
    promiseFn: API.getTextBanner,
    orderType: orderType,
    watch: orderType,
  })
  const bannerCount = data?.items.length || 0

  const bannerRef = useRef<HTMLDivElement>(null)
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)
  const dropdownIconRef = useRef<SVGSVGElement>(null)

  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false)
  const [selectedBanner, setSelectedBanner] = useState<GetTextBannerItem>()

  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)

  const handleClickBanner = (selectedBanner: GetTextBannerItem) => {
    if (!selectedBanner?.link || selectedBanner.linkType === TextBannerLinkTypes.NONE) return
    try {
      API.patchTextBannerCount(selectedBanner.bannerId)
    } catch (error) {
      console.log(error)
    }

    if (marketingGTM?.checkAvailability()) {
      const queryStringPrefix = selectedBanner.link.indexOf('?') >= 0 ? '&' : '?'
      selectedBanner.link = selectedBanner.link.concat(
        `${queryStringPrefix}_gl=${marketingGTM.getGl()}`,
      )
    }

    const isOpenLink = selectedBanner.linkType === TextBannerLinkTypes.OPEN
    moveToLink({
      link: selectedBanner.link,
      type: isOpenLink ? LINK_MOVE_TYPE.NEW_WINDOW : LINK_MOVE_TYPE.REDIRECT,
    })
  }
  const [visibilityAnimation, setVisibilityAnimation] = useState(false)

  useOutsideClickEffect(
    [
      bannerRef.current,
      dropdownButtonRef.current,
      dropdownIconRef.current as unknown as HTMLElement,
    ],
    () => setIsOpenDropdown(false),
  )

  useEffect(() => {
    if (isOpenDropdown) {
      setVisibilityAnimation(true)
    } else {
      setTimeout(() => {
        setVisibilityAnimation(false)
      }, 400)
    }
  }, [isOpenDropdown])

  useEffect(() => {
    if (!data) return
    setSelectedBanner(data.items[0])
  }, [data])

  if (isLoading) {
    return (
      <StyledContainer>
        <SkeletonBanner />
      </StyledContainer>
    )
  }

  if (!selectedBanner || !data) return null

  return (
    <StyledContainer ref={bannerRef} banner={selectedBanner}>
      <StyledWrapper banner={selectedBanner}>
        <StyledSelectedBanner
          banner={selectedBanner}
          onClick={() => handleClickBanner(selectedBanner)}
        >
          {selectedBanner.bannerText}
        </StyledSelectedBanner>
        {bannerCount > 1 && (
          <StyledDropdownButton
            ref={dropdownButtonRef}
            onClick={() => setIsOpenDropdown(!isOpenDropdown)}
          >
            {isOpenDropdown ? (
              <IconChevronUp ref={dropdownIconRef} />
            ) : (
              <IconChevronDown ref={dropdownIconRef} />
            )}
          </StyledDropdownButton>
        )}
      </StyledWrapper>
      {visibilityAnimation && (
        <StyledDropdownContainer
          className={`${isOpenDropdown ? 'dropdown-fade-id' : 'dropdown-fade-out'}`}
        >
          <StyledDropdown className="scrollbar">
            {data.items.map((item: GetTextBannerItem) => {
              if (item.bannerId === selectedBanner.bannerId) return null
              return (
                <StyledDropdownItem
                  id="dropdown-item"
                  key={item.bannerId}
                  banner={item}
                  onClick={() => {
                    setSelectedBanner(item)
                    setIsOpenDropdown(false)
                  }}
                >
                  {item.bannerText}
                </StyledDropdownItem>
              )
            })}
          </StyledDropdown>
        </StyledDropdownContainer>
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled.div<{ banner?: GetTextBannerItem }>`
  position: relative;
  display: flex;
  max-width: 1200px;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background-color: ${(props) => props.banner?.badge.bg};
  padding: 0px 12px;
`

const StyledWrapper = styled.div<{ banner: GetTextBannerItem }>`
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 750px;
  width: 100%;
  height: 60px;
  padding: 0px 30px;
  color: ${(props) => props.banner.badge.color};

  @media screen and (max-width: 517px) {
    padding: 0px 16px;
  }
`

const StyledSelectedBanner = styled.div<{ banner: GetTextBannerItem }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 12px 0px;
  font-size: 21px;
  font-weight: 500;
  line-height: 125%; /* 26.25px */
  cursor: ${({ banner }) =>
    banner.linkType === TextBannerLinkTypes.NONE ? 'default;' : 'pointer;'};

  @media screen and (max-width: 517px) {
    font-size: 12px;
  }
`

const StyledDropdownButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  padding: 8px;
  border-radius: 5.333px;
  border: 1.333px solid rgba(0, 0, 0, 0.2);
  background: #fff;
  cursor: pointer;
  margin-left: 6px;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`

const StyledDropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 750px;
  width: 100%;

  @keyframes dropdown-fade-in {
    0% {
      transform: translateY(-100%);
    }

    100% {
      transform: translateY(0);
    }
  }

  &.dropdown-fade-id {
    overflow: hidden;
  }

  &.dropdown-fade-id > div {
    animation: dropdown-fade-in 0.4s ease;
  }

  @keyframes dropdown-fade-out {
    0% {
      transform: translateY(0);
    }

    100% {
      transform: translateY(-100%);
    }
  }

  &.dropdown-fade-out {
    overflow: hidden;
  }

  &.dropdown-fade-out > div {
    animation: dropdown-fade-out 0.4s ease;
    animation-fill-mode: forwards;
  }

  @keyframes dropdown-fade-in {
    0% {
      transform: translateY(-100%);
    }

    100% {
      transform: translateY(0);
    }
  }

  &.dropdown-fade-id {
    overflow: hidden;
  }

  &.dropdown-fade-id > div {
    animation: dropdown-fade-in 0.4s ease;
  }

  @keyframes dropdown-fade-out {
    0% {
      transform: translateY(0);
    }

    100% {
      transform: translateY(-100%);
    }
  }

  &.dropdown-fade-out {
    overflow: hidden;
  }

  &.dropdown-fade-out > div {
    animation: dropdown-fade-out 0.4s ease;
    animation-fill-mode: forwards;
  }
`

const StyledDropdown = styled.div`
  width: 100%;
  max-height: 300px;
  background-color: #ffffff;
  overflow-y: scroll;
  border-radius: 0px 0px 10px 10px;
  border-right: 2px solid #fff;
  border-bottom: 2px solid #fff;
  border-left: 2px solid #fff;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.3);
  z-index: 20;
`

const StyledDropdownItem = styled.div<{ banner: GetTextBannerItem }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 60px;
  padding: 0px 30px;
  cursor: pointer;
  font-size: 21px;
  font-weight: 500;
  line-height: 125%; /* 26.25px */
  background-color: ${(props) => props.banner.badge.bg};
  color: ${(props) => props.banner.badge.color};

  @media screen and (max-width: 517px) {
    font-size: 12px;
    padding: 0px 16px;
  }
`

export default SectionTextBanner
